import { useId, useState } from 'react';
import { useBlocker, useNavigate, useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { Checkbox, FormField, Input, SearchField, Select, Switch, Textarea } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { pode } from '@/session/sessao';
import type { PreferenciaSalva, ValorComposto, ValorDePreferencia } from '@/data/cadastros';
import { PREFERENCIAS, SECOES_DE_PREFERENCIA, mesmoValor } from '@/data/preferencias';
import type { OpcaoDePreferencia, Preferencia, SecaoDePreferencia } from '@/data/preferencias';

/** As opções que são cadastro da clínica — contas, métodos e categorias de verdade. */
export interface OpcoesDasPreferencias {
    contas: OpcaoDePreferencia[];
    metodos: OpcaoDePreferencia[];
    categoriasReceita: OpcaoDePreferencia[];
    categoriasDespesa: OpcaoDePreferencia[];
}

const CAMPOS_DE_CADASTRO = ['Nome completo', 'Telefone', 'E-mail', 'CPF', 'Data de nascimento', 'Endereço', 'Como nos conheceu'];
const CANAIS: Record<string, string> = { whatsapp: 'WhatsApp', sms: 'SMS', email: 'E-mail' };
const QUEM_ABSORVE: Record<string, string> = { clinica: 'Clínica', profissional: 'Profissional', proporcional: 'Proporcional' };
const MARGENS: Record<string, string> = { estreita: 'Estreita', normal: 'Normal', larga: 'Larga' };

function normalizar(texto: string): string {
    return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function opcoesDa(p: Preferencia, opcoes: OpcoesDasPreferencias): OpcaoDePreferencia[] {
    if (p.fonte === 'contas') return opcoes.contas;
    if (p.fonte === 'metodos') return opcoes.metodos;
    if (p.fonte === 'categorias-receita') return opcoes.categoriasReceita;
    if (p.fonte === 'categorias-despesa') return opcoes.categoriasDespesa;
    return p.opcoes ?? [];
}

function valorInicialComposto(p: Preferencia): ValorComposto {
    if (p.editor === 'lembrete') {
        return {
            ativo: p.padraoRotulo === 'Habilitado', canal: 'whatsapp', antecedencia: 24, unidade: 'horas',
            mensagem: 'Olá {paciente}, confirmando seu atendimento de {data} às {hora} com {profissional}. Responda SIM para confirmar.',
        };
    }
    if (p.editor === 'campos') return { campos: p.chave === 'ct.obrigatorios' ? ['Nome completo', 'Telefone'] : [] };
    if (p.editor === 'comissao') return { quem: 'clinica' };
    if (p.editor === 'impressao') return { logotipo: true, rodape: true, margens: 'normal' };
    return { texto: '' };
}

function resumoComposto(p: Preferencia, v: ValorComposto): string {
    if (p.editor === 'lembrete') {
        return v.ativo
            ? `${String(v.antecedencia)} ${String(v.unidade)} antes · ${CANAIS[String(v.canal)] ?? String(v.canal)}`
            : 'Desabilitado';
    }
    if (p.editor === 'campos') {
        const campos = Array.isArray(v.campos) ? v.campos : [];
        return campos.length ? campos.join(', ') : 'Nenhum';
    }
    if (p.editor === 'comissao') return QUEM_ABSORVE[String(v.quem)] ?? String(v.quem);
    if (p.editor === 'impressao') {
        return `${v.logotipo ? 'Com' : 'Sem'} logotipo · margens ${MARGENS[String(v.margens)]?.toLowerCase() ?? String(v.margens)}`;
    }
    return String(v.texto ?? '').trim() ? 'Preenchido' : 'Vazio';
}

function rotuloDoValor(p: Preferencia, valor: ValorDePreferencia | null, opcoes: OpcoesDasPreferencias): string {
    if (valor === null) return p.padraoRotulo ?? 'Sem padrão';
    if (typeof valor === 'object') return resumoComposto(p, valor);
    if (typeof valor === 'boolean') return valor ? 'Ligado' : 'Desligado';
    if (p.tipo === 'select') return opcoesDa(p, opcoes).find((o) => o.valor === String(valor))?.rotulo ?? String(valor);
    if (p.tipo === 'text' && valor === '') return 'Vazio';
    return `${String(valor)}${p.unidade ? ` ${p.unidade}` : ''}`;
}

export interface PreferenciasDoSistemaProps {
    salvas: PreferenciaSalva[];
    opcoes: OpcoesDasPreferencias;
}

/**
 * Preferências do sistema — porte do `PreferenceForm` do protótipo.
 *
 * Nada grava a cada clique: a pessoa muda o que quiser e salva uma vez. Sair
 * com alteração pendente pergunta antes, porque preferência "mudada" que não
 * foi salva é a pior das duas: parece aplicada e não está.
 */
export function PreferenciasDoSistema({ salvas, opcoes }: PreferenciasDoSistemaProps) {
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const idBusca = useId();

    /* `null` no rascunho é "voltar ao padrão". */
    const [rascunho, setRascunho] = useState<Record<string, ValorDePreferencia | null>>({});
    const [secao, setSecao] = useState<SecaoDePreferencia | 'todas'>('todas');
    const [busca, setBusca] = useState('');
    const [compondo, setCompondo] = useState<Preferencia | null>(null);
    const [salvando, setSalvando] = useState(false);

    const salvo = (chave: string): ValorDePreferencia | undefined => salvas.find((s) => s.chave === chave)?.valor;
    const emVigor = (p: Preferencia): ValorDePreferencia | null =>
        (p.chave in rascunho ? (rascunho[p.chave] ?? p.padrao) : (salvo(p.chave) ?? p.padrao));

    const pendentes = Object.entries(rascunho).filter(([chave, valor]) => {
        const p = PREFERENCIAS.find((x) => x.chave === chave);
        return !mesmoValor(salvo(chave) ?? p?.padrao ?? null, valor ?? p?.padrao ?? null);
    });
    const sujo = pendentes.length > 0;

    const bloqueio = useBlocker(({ currentLocation, nextLocation }) => sujo && currentLocation.pathname !== nextLocation.pathname);

    const mudar = (p: Preferencia, valor: ValorDePreferencia | null) => setRascunho((r) => ({ ...r, [p.chave]: valor }));

    const salvar = async () => {
        setSalvando(true);
        try {
            const mudancas = pendentes.map(([chave, valor]) => {
                const p = PREFERENCIAS.find((x) => x.chave === chave);
                /* Valor igual ao padrão é restaurar: apaga em vez de gravar. */
                const final = valor === null || mesmoValor(valor, p?.padrao ?? null) ? null : valor;
                return { chave, valor: final };
            });
            const novas = await dados().salvarPreferencias(mudancas);
            await revalidator.revalidate();
            setRascunho({});
            aviso({ title: 'Preferências salvas', description: `${novas.length} diferentes do padrão` });
        } catch (e) {
            aviso({
                title: 'Não foi possível salvar as preferências',
                description: e instanceof Error ? e.message : 'Tente de novo.',
                tone: 'danger',
            });
        } finally {
            setSalvando(false);
        }
    };

    const termo = normalizar(busca.trim());
    const visiveis = PREFERENCIAS.filter((p) => (termo
        ? normalizar(`${p.rotulo} ${p.descricao ?? ''}`).includes(termo)
        : secao === 'todas' || p.secao === secao));
    const semEfeito = PREFERENCIAS.filter((p) => !p.efeito).length;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Alert tone="info" title={`${semEfeito} de ${PREFERENCIAS.length} preferências ainda não mudam nada no sistema`}>
                A escolha fica guardada desde já, e passa a valer quando o módulo que ela controla for ligado a ela. As que já
                valem dizem onde.
            </Alert>

            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <nav aria-label="Seções das preferências" style={{ flex: '0 0 200px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {[{ id: 'todas' as const, rotulo: 'Todas' }, ...SECOES_DE_PREFERENCIA].map((s) => {
                        const ativa = !termo && secao === s.id;
                        return (
                            <button
                                key={s.id} type="button" aria-current={ativa ? 'true' : undefined}
                                onClick={() => { setBusca(''); setSecao(s.id); }}
                                style={{
                                    textAlign: 'left', border: 0, cursor: 'pointer', padding: '8px 10px', borderRadius: 'var(--radius-sm)',
                                    background: ativa ? 'var(--surface-selected)' : 'transparent',
                                    color: ativa ? 'var(--text-brand)' : 'var(--text-body)',
                                    fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: ativa ? 600 : 500,
                                }}
                            >
                                {s.rotulo}
                            </button>
                        );
                    })}
                </nav>

                <div style={{ flex: '1 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <label htmlFor={idBusca} className="vec-sr-only">Buscar preferência</label>
                    <SearchField value={busca} onChange={setBusca} placeholder="Buscar preferência" ariaLabel="Buscar preferência" />

                    {SECOES_DE_PREFERENCIA.map((s) => {
                        const daSecao = visiveis.filter((p) => p.secao === s.id);
                        if (!daSecao.length) return null;
                        return (
                            <Card key={s.id} title={s.rotulo} padding={0}>
                                {daSecao.map((p, i) => (
                                    <LinhaDaPreferencia
                                        key={p.chave} preferencia={p} primeira={i === 0}
                                        valor={emVigor(p)} opcoes={opcoes}
                                        onMudar={(v) => mudar(p, v)}
                                        onRestaurar={() => mudar(p, null)}
                                        onCompor={() => setCompondo(p)}
                                    />
                                ))}
                            </Card>
                        );
                    })}
                    {!visiveis.length && (
                        <p style={{ margin: 0, padding: 20, fontSize: 13.5, color: 'var(--text-secondary)' }}>Nenhuma preferência com “{busca}”.</p>
                    )}
                </div>
            </div>

            {sujo && (
                <div
                    role="region" aria-label="Alterações pendentes"
                    style={{
                        position: 'sticky', bottom: 0, zIndex: 5, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                        padding: '12px 16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
                        boxShadow: 'var(--shadow-md)',
                    }}
                >
                    <Icon name="circle-dot" size={15} style={{ color: 'var(--warning-strong)' }} />
                    <span style={{ fontSize: 13.5, color: 'var(--text-body)' }}>
                        {pendentes.length === 1 ? '1 alteração não salva' : `${pendentes.length} alterações não salvas`}
                    </span>
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                        <Button variant="ghost" onClick={() => setRascunho({})} disabled={salvando}>Descartar</Button>
                        <Button onClick={() => { void salvar(); }} disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar preferências'}</Button>
                    </span>
                </div>
            )}

            {compondo && (
                <EditorDaComposta
                    preferencia={compondo}
                    valor={emVigor(compondo)}
                    onAplicar={(v) => { mudar(compondo, v); setCompondo(null); }}
                    onFechar={() => setCompondo(null)}
                />
            )}

            <Modal
                open={bloqueio.state === 'blocked'}
                title="Sair sem salvar?"
                description="Você alterou preferências e ainda não salvou."
                width={440}
                onClose={() => bloqueio.reset?.()}
                footer={(
                    <>
                        <Button variant="ghost" onClick={() => bloqueio.reset?.()}>Voltar e salvar</Button>
                        <Button variant="danger" onClick={() => { setRascunho({}); bloqueio.proceed?.(); }}>Sair sem salvar</Button>
                    </>
                )}
            />
        </div>
    );
}

interface LinhaDaPreferenciaProps {
    preferencia: Preferencia;
    primeira: boolean;
    valor: ValorDePreferencia | null;
    opcoes: OpcoesDasPreferencias;
    onMudar: (valor: ValorDePreferencia | null) => void;
    onRestaurar: () => void;
    onCompor: () => void;
}

function LinhaDaPreferencia({ preferencia: p, primeira, valor, opcoes, onMudar, onRestaurar, onCompor }: LinhaDaPreferenciaProps) {
    const idRotulo = useId();
    const alterado = !mesmoValor(valor, p.padrao);
    const travada = p.soProprietaria === true && !pode('*');

    let controle;
    if (p.tipo === 'boolean') {
        controle = <Switch checked={valor === true} onChange={(v) => onMudar(v)} ariaLabel={p.rotulo} />;
    } else if (p.tipo === 'select') {
        const lista = opcoesDa(p, opcoes);
        controle = (
            <Select
                aria-labelledby={idRotulo} value={valor === null ? '' : String(valor)}
                onChange={(v) => onMudar(v === '' ? null : v)}
                options={lista.map((o) => ({ value: o.valor, label: o.rotulo }))}
                placeholder={p.fonte ? (lista.length ? 'Sem padrão' : 'Nada cadastrado') : undefined}
                disabled={travada}
                style={{ minWidth: 'min(220px, 100%)' }}
            />
        );
    } else if (p.tipo === 'number') {
        controle = (
            <Input
                aria-labelledby={idRotulo} type="number" min={0} inputMode="numeric"
                value={valor === null ? '' : String(valor)}
                onChange={(t) => {
                    if (t === '') { onMudar(null); return; }
                    const n = Number(t);
                    if (Number.isFinite(n) && n >= 0) onMudar(Math.round(n));
                }}
                suffix={p.unidade} style={{ width: 150 }}
            />
        );
    } else if (p.tipo === 'text') {
        controle = <Input aria-labelledby={idRotulo} value={typeof valor === 'string' ? valor : ''} onChange={(t) => onMudar(t)} style={{ width: 220 }} />;
    } else {
        controle = (
            <Button size="sm" variant="secondary" iconRight={<Icon name="chevron-right" size={13} />} onClick={onCompor} aria-describedby={idRotulo}>
                {rotuloDoValor(p, valor, opcoes)}
            </Button>
        );
    }

    return (
        <div
            style={{
                display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', padding: '14px 20px',
                borderTop: primeira ? 'none' : '1px solid var(--border-subtle)',
            }}
        >
            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span id={idRotulo} style={{ fontFamily: 'var(--font-ui)', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{p.rotulo}</span>
                    {alterado && <Badge tone="info">Alterado</Badge>}
                    {!p.efeito && <Badge tone="neutral">Ainda sem efeito</Badge>}
                </div>
                <p style={{ margin: '3px 0 0', fontSize: 12.5, lineHeight: '18px', color: 'var(--text-secondary)' }}>
                    {p.descricao ? `${p.descricao} ` : ''}(Padrão: {rotuloDoValor(p, p.padrao, opcoes)})
                </p>
                {travada && (
                    <p style={{ margin: '3px 0 0', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--text-tertiary)' }}>
                        <Icon name="lock" size={12} />Só a proprietária altera esta opção.
                    </p>
                )}
                {p.efeito && (
                    <p style={{ margin: '3px 0 0', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--success-strong)' }}>
                        <Icon name="check" size={12} />{p.efeito}
                    </p>
                )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {alterado && !travada && <Button size="sm" variant="ghost" onClick={onRestaurar}>Restaurar</Button>}
                {controle}
            </div>
        </div>
    );
}

interface EditorDaCompostaProps {
    preferencia: Preferencia;
    valor: ValorDePreferencia | null;
    onAplicar: (valor: ValorComposto) => void;
    onFechar: () => void;
}

/** A preferência composta abre formulário próprio, nunca expande na lista. */
function EditorDaComposta({ preferencia: p, valor, onAplicar, onFechar }: EditorDaCompostaProps) {
    const navigate = useNavigate();
    const inicial = valor !== null && typeof valor === 'object' ? valor : valorInicialComposto(p);
    const [v, setV] = useState<ValorComposto>(inicial);
    const idCanal = useId();
    const idAntecedencia = useId();
    const idUnidade = useId();
    const idMensagem = useId();
    const idQuem = useId();
    const idMargens = useId();
    const idTexto = useId();
    const pegar = (campo: string, valorNovo: string | number | boolean | string[]) => setV((x) => ({ ...x, [campo]: valorNovo }));
    const alterado = !mesmoValor(v, inicial);

    /* O lembrete de agendamento é a régua de Notificações: um formulário aqui
       seria o segundo lugar para a mesma coisa. */
    if (p.chave === 'ag.lembrete-agendamento') {
        return (
            <Modal
                open title={p.rotulo} description="Preferência composta" width={520} onClose={onFechar}
                footer={(
                    <>
                        <Button variant="ghost" onClick={onFechar}>Fechar</Button>
                        <Button iconLeft={<Icon name="bell" size={14} />} onClick={() => { onFechar(); void navigate('/app/configuracoes/notificacoes'); }}>
                            Abrir a régua de lembretes
                        </Button>
                    </>
                )}
            >
                <Alert tone="info" title="Este lembrete já está configurado em Notificações">
                    Canal, antecedência e texto de cada lembrete do agendamento ficam na régua. É ela que a agenda lê.
                </Alert>
            </Modal>
        );
    }

    return (
        <Modal
            open title={p.rotulo} description="Preferência composta" width={560} onClose={onFechar}
            dirty={alterado} onSave={() => onAplicar(v)} saveLabel="Aplicar"
            footer={(
                <>
                    <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
                    <Button onClick={() => onAplicar(v)}>Aplicar</Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {p.descricao && <Alert tone="neutral" title={p.descricao} />}
                {!p.efeito && (
                    <Alert tone="warning" title="Ainda sem efeito">
                        A configuração fica guardada, mas nenhum módulo a usa ainda.
                    </Alert>
                )}

                {p.editor === 'lembrete' && (
                    <>
                        <Switch checked={v.ativo === true} onChange={(x) => pegar('ativo', x)} label="Lembrete ativo" description="Desligue para suspender sem perder o texto" />
                        <FormField label="Canal" htmlFor={idCanal}>
                            <Select id={idCanal} value={String(v.canal)} onChange={(x) => pegar('canal', x)}
                                options={[{ value: 'whatsapp', label: 'WhatsApp' }, { value: 'sms', label: 'SMS' }, { value: 'email', label: 'E-mail' }]} />
                        </FormField>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                            <FormField label="Antecedência" htmlFor={idAntecedencia}>
                                <Input id={idAntecedencia} type="number" min={0} value={String(v.antecedencia)}
                                    onChange={(x) => pegar('antecedencia', Math.max(0, Math.round(Number(x) || 0)))} />
                            </FormField>
                            <FormField label="Unidade" htmlFor={idUnidade}>
                                <Select id={idUnidade} value={String(v.unidade)} onChange={(x) => pegar('unidade', x)}
                                    options={[{ value: 'minutos', label: 'Minutos' }, { value: 'horas', label: 'Horas' }, { value: 'dias', label: 'Dias' }]} />
                            </FormField>
                        </div>
                        <FormField label="Mensagem" htmlFor={idMensagem} hint="Use {paciente}, {data}, {hora} e {profissional}">
                            <Textarea id={idMensagem} rows={4} value={String(v.mensagem ?? '')} onChange={(x) => pegar('mensagem', x)} />
                        </FormField>
                    </>
                )}

                {p.editor === 'campos' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {CAMPOS_DE_CADASTRO.map((c) => {
                            const lista = Array.isArray(v.campos) ? v.campos : [];
                            return (
                                <Checkbox
                                    key={c} label={c} checked={lista.includes(c)}
                                    onChange={(marcado) => pegar('campos', marcado ? [...lista, c] : lista.filter((x) => x !== c))}
                                />
                            );
                        })}
                    </div>
                )}

                {p.editor === 'comissao' && (
                    <FormField label="Quem absorve" htmlFor={idQuem} hint="Vale para todos os profissionais, salvo regra individual">
                        <Select id={idQuem} value={String(v.quem)} onChange={(x) => pegar('quem', x)}
                            options={Object.entries(QUEM_ABSORVE).map(([value, label]) => ({ value, label }))} />
                    </FormField>
                )}

                {p.editor === 'impressao' && (
                    <>
                        <Switch checked={v.logotipo === true} onChange={(x) => pegar('logotipo', x)} label="Exibir logotipo no cabeçalho" />
                        <Switch checked={v.rodape === true} onChange={(x) => pegar('rodape', x)} label="Exibir endereço e telefone no rodapé" />
                        <FormField label="Margens" htmlFor={idMargens}>
                            <Select id={idMargens} value={String(v.margens)} onChange={(x) => pegar('margens', x)}
                                options={Object.entries(MARGENS).map(([value, label]) => ({ value, label }))} />
                        </FormField>
                    </>
                )}

                {p.editor === 'texto' && (
                    <FormField label="Texto fixo" htmlFor={idTexto} hint="Aparece no rodapé de todo orçamento">
                        <Textarea id={idTexto} rows={5} value={String(v.texto ?? '')} onChange={(x) => pegar('texto', x)}
                            placeholder="Ex.: proposta válida por 30 dias, sujeita a avaliação presencial." />
                    </FormField>
                )}
            </div>
        </Modal>
    );
}
